import {
  Banner, DataTable, InlineGrid, Layout, Card,
  Page, Spinner, Text,
} from '../components/admin-ui';
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import PerformanceFilters, { money, performanceSearch, usePerformanceFilters } from "../componens/performanceFilters";
import { admin_store_performance } from "../api";

export default function StorePerformance() {
  const [applied, setApplied] = usePerformanceFilters();
  const location = useLocation(), navigate = useNavigate(), restored = useRef(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (report && !restored.current && location.state?.overviewScroll !== undefined) {
      restored.current = true;
      window.scrollTo(0, location.state.overviewScroll);
    }
  }, [report, location.state]);

  useEffect(() => {
    let active = true;
    setLoading(true); setError("");
    admin_store_performance(applied).then((response) => {
      if (!active) return;
      if (response.status === 200) setReport(response.data);
      else setError(response.msg || "Unable to load store performance.");
    }).catch((requestError) => {
      if (active) setError(requestError?.response?.data?.msg || "Unable to load store performance.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [applied, retry]);

  const openStore = (event, store) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate("/Performance/" + store.shop_id + performanceSearch(report.filters || applied), {
      state: { overviewSearch: performanceSearch(applied), overviewScroll: window.scrollY },
    });
  };

  const rows = (report?.stores || []).map((store) => [
    <div><Link className="sa-store-link" onClick={(event) => openStore(event, store)} to={"/Performance/" + store.shop_id + performanceSearch(report.filters || applied)} state={{ overviewSearch: performanceSearch(applied), overviewScroll: window.scrollY }}>{store.shop_name || "Shop " + store.shop_id}</Link>{store.app_name && <Text as="p" tone="subdued">{store.app_name}</Text>}</div>,
    store.is_active ? "Active" : "Inactive",
    store.order_count,
    store.confirmed_order_count,
    money(store.confirmed_amounts),
    <Link className="sa-text-link" onClick={(event) => openStore(event, store)} to={"/Performance/" + store.shop_id + performanceSearch(report.filters || applied)} state={{ overviewSearch: performanceSearch(applied), overviewScroll: window.scrollY }} aria-label={"View performance for " + store.shop_name}>View performance →</Link>,
  ]);

  return (
    <Page title="Store performance" subtitle="Order performance by source Shopify store">
      <Layout>
        {error && <Layout.Section><Banner tone="critical" title="Unable to load report">{error} <button className="sa-button" onClick={() => setRetry((n) => n + 1)}>Retry report</button></Banner></Layout.Section>}
        <Layout.Section>
          <PerformanceFilters applied={applied} onApply={setApplied} reportFilters={report?.filters} loading={loading} />
        </Layout.Section>
        <Layout.Section>
          <Banner>
            Counts include orders created in the selected UTC dates. Confirmed online orders have status 1; confirmed COD orders have status 3 (accepted). Amounts are grouped by currency and represent order value, not signed delivery or repayment.
          </Banner>
        </Layout.Section>
        <Layout.Section>
          {loading && !report ? <Spinner accessibilityLabel="Loading store performance" /> : <InlineGrid columns={{ xs: 1, sm: 3 }} gap="400">
            <Card title="Orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.order_count ?? 0}</Text></Card>
            <Card title="Confirmed orders" sectioned><Text as="p" variant="headingLg">{report?.totals?.confirmed_order_count ?? 0}</Text></Card>
            <Card title="Confirmed order value" sectioned><Text as="p" variant="headingMd">{money(report?.totals?.confirmed_amounts)}</Text></Card>
          </InlineGrid>}
        </Layout.Section>
        <Layout.Section>
          <Card title="Performance by store">
            <DataTable
              columnContentTypes={["text", "text", "numeric", "numeric", "text", "text"]}
              headings={["Shop", "Status", "Orders", "Confirmed", "Confirmed order value", ""]}
              rows={rows}
              loading={loading}
              emptyState={<Text as="p" tone="subdued">No stores or orders match this period.</Text>}
            />
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
